/**
 * Cart drawer behaviour.
 *
 * Everything is delegated from document, so re-rendering the drawer's markup
 * via the Section Rendering API never leaves stale or duplicated listeners.
 *
 * The drawer is an enhancement: every entry point it hijacks (the product form,
 * the cart link) still works on its own if this script fails to load, so the
 * markup stays a real form and a real link.
 */
(() => {
  const SECTION = 'cart-drawer';

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const content = () => document.querySelector('[data-cart-drawer-content]');

  const setBusy = (busy) => {
    const el = drawer();
    if (!el) return;
    if (busy) {
      el.setAttribute('data-busy', '');
    } else {
      el.removeAttribute('data-busy');
    }
  };

  const open = () => {
    const el = drawer();
    if (!el) return;
    if (!el.open) el.showModal();
  };

  /** Re-render the drawer from the server so Liquid stays the source of truth. */
  const refresh = async () => {
    const response = await fetch(`${window.Shopify.routes.root}?sections=${SECTION}`);
    if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

    const data = await response.json();
    const html = new DOMParser().parseFromString(data[SECTION], 'text/html');
    const next = html.querySelector('[data-cart-drawer-content]');
    const current = content();
    if (next && current) current.innerHTML = next.innerHTML;

    syncCount(html);
  };

  /** Keep the header badge in step with the drawer. */
  const syncCount = (doc) => {
    const countEl = doc.querySelector('.cart-drawer__count');
    const text = countEl ? countEl.textContent.trim() : '';
    const digits = text.match(/\d+/);
    const count = digits ? parseInt(digits[0], 10) : 0;

    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  const post = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || `Request failed: ${response.status}`);
    }
    return response.json();
  };

  const changeLine = async (line, quantity) => {
    setBusy(true);
    try {
      await post(`${window.Shopify.routes.root}cart/change.js`, { line, quantity });
      await refresh();
    } catch (error) {
      // Fall back to the real cart page rather than leave a wrong quantity.
      console.error('[cart-drawer]', error);
      window.location.href = `${window.Shopify.routes.root}cart`;
    } finally {
      setBusy(false);
    }
  };

  // Add to cart: intercept the product form.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || !drawer()) return;

    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error(`Add failed: ${response.status}`);

      await refresh();
      open();
    } catch (error) {
      // Let the browser do the normal, non-JS submit instead of failing silently.
      console.error('[cart-drawer]', error);
      form.submit();
    } finally {
      if (button) button.disabled = false;
    }
  });

  // Cart link opens the drawer instead of navigating.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (trigger && drawer()) {
      event.preventDefault();
      refresh().catch(() => {});
      open();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      drawer()?.close();
      return;
    }

    const minus = event.target.closest('[data-cart-minus]');
    const plus = event.target.closest('[data-cart-plus]');
    if (minus || plus) {
      const wrap = (minus || plus).closest('[data-quantity]');
      const input = wrap?.querySelector('[data-cart-qty]');
      if (!input) return;
      const next = Math.max(0, parseInt(input.value, 10) + (plus ? 1 : -1));
      changeLine(parseInt(input.dataset.line, 10), next);
    }
  });

  // Typed quantity.
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-qty]');
    if (!input) return;
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(parseInt(input.dataset.line, 10), quantity);
  });

  // Clicking the backdrop (outside the panel) closes the dialog.
  document.addEventListener('click', (event) => {
    const el = drawer();
    if (!el || !el.open || event.target !== el) return;
    el.close();
  });

  /**
   * Public seam for anything else that adds to the cart (the bundle section,
   * for one) so it can hand off here instead of reimplementing refresh + open.
   */
  document.addEventListener('cart:added', async () => {
    setBusy(true);
    try {
      await refresh();
      open();
    } catch (error) {
      console.error('[cart-drawer]', error);
    } finally {
      setBusy(false);
    }
  });
})();
